/**
 * tdesign v1.0.3
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _objectWithoutProperties } from './dep-92728dc5.js';
import { _ as _defineProperty } from './dep-46de7b2e.js';
import { _ as _slicedToArray } from './dep-e43cf21c.js';
import React, { useState, useEffect, useCallback } from 'react';
import { i as isArray_1 } from './dep-77e34309.js';
import { i as isFunction_1 } from './dep-f10515e8.js';
import { ImageErrorIcon, MirrorIcon, RotationIcon, ImageIcon } from 'tdesign-icons-react';
import classNames from 'classnames';
import { TooltipLite } from '../tooltip/index.js';
import useConfig from '../hooks/useConfig.js';
import { useLocaleReceiver } from '../locale/LocalReceiver.js';
import { downloadFile } from '../image-viewer/utils.js';
import { Dialog } from '../dialog/index.js';
import useMirror from '../image-viewer/hooks/useMirror.js';
import usePosition from '../image-viewer/hooks/usePosition.js';
import useIndex from '../image-viewer/hooks/useIndex.js';
import useRotate from '../image-viewer/hooks/useRotate.js';
import useScale from '../image-viewer/hooks/useScale.js';
import useGlobalIcon from '../hooks/useGlobalIcon.js';
import useIconMap from '../image-viewer/hooks/useIconMap.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var ImageModelMiniContent = function ImageModelMiniContent(props) {
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  return /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer-mini__content")
  }, /* @__PURE__ */React.createElement(ImageModelItem, {
    rotateZ: props.rotateZ,
    scale: props.scale,
    mirror: props.mirror,
    src: props.currentImage.mainImage,
    preSrc: props.currentImage.thumbnail,
    errorText: props.errorText
  }));
};
var ImageModelMini = function ImageModelMini(props) {
  var visible = props.visible,
    title = props.title,
    scale = props.scale,
    currentImage = props.currentImage,
    draggable = props.draggable,
    onZoomOut = props.onZoomOut,
    onZoom = props.onZoom,
    onClose = props.onClose,
    onRotate = props.onRotate,
    onMirror = props.onMirror,
    onReset = props.onReset,
    tipText = props.tipText;
  var _useConfig2 = useConfig(),
    classPrefix = _useConfig2.classPrefix;
  var footer = /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer-mini__footer")
  }, /* @__PURE__ */React.createElement(ImageViewerUtils, {
    onZoom: onZoom,
    onZoomOut: onZoomOut,
    scale: scale,
    currentImage: currentImage,
    onRotate: onRotate,
    onMirror: onMirror,
    onReset: onReset,
    tipText: tipText
  }));
  return /* @__PURE__ */React.createElement(Dialog, {
    className: "".concat(classPrefix, "-image-viewer__dialog"),
    draggable: draggable,
    visible: visible,
    width: "min(90vw, 1000px)",
    placement: "center",
    mode: "modeless",
    closeOnOverlayClick: false,
    cancelBtn: null,
    confirmBtn: null,
    header: /* @__PURE__ */React.createElement("span", {
      className: "".concat(classPrefix, "-image-viewer__dialog-title")
    }, title),
    footer: footer,
    onClose: onClose
  }, /* @__PURE__ */React.createElement(ImageModelMiniContent, _objectSpread({}, props)));
};

var _excluded = ["closeOnOverlay", "showOverlay", "zIndex", "images", "isMini", "imageScale", "viewerScale", "closeBtn", "draggable", "onOpen", "onClose", "visible", "title"];
var ImageError = function ImageError(_ref) {
  var errorText = _ref.errorText;
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var _useGlobalIcon = useGlobalIcon({
      ImageErrorIcon: ImageErrorIcon
    }),
    ImageErrorIcon$1 = _useGlobalIcon.ImageErrorIcon;
  return /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__img-error")
  }, /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__img-error-content")
  }, /* @__PURE__ */React.createElement(ImageErrorIcon$1, {
    size: "4em"
  }), /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__img-error-text")
  }, errorText)));
};
var ImageModelItem = function ImageModelItem(_ref2) {
  var rotateZ = _ref2.rotateZ,
    scale = _ref2.scale,
    src = _ref2.src,
    preSrc = _ref2.preSrc,
    mirror = _ref2.mirror,
    errorText = _ref2.errorText;
  var _useConfig2 = useConfig(),
    classPrefix = _useConfig2.classPrefix;
  var _usePosition = usePosition({
      initPosition: [0, 0]
    }),
    _usePosition2 = _slicedToArray(_usePosition, 2),
    position = _usePosition2[0],
    _onMouseDown = _usePosition2[1];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    loaded = _useState2[0],
    setLoaded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  var imgStyle = {
    transform: "rotateZ(".concat(rotateZ, "deg) scale(").concat(scale, ")"),
    display: !preSrc || loaded ? "block" : "none"
  };
  var preImgStyle = {
    transform: "rotateZ(".concat(rotateZ, "deg) scale(").concat(scale, ")"),
    display: !loaded ? "block" : "none"
  };
  var boxStyle = {
    transform: "translate(".concat(position[0], "px, ").concat(position[1], "px) scale(").concat(mirror, ", 1)")
  };
  useEffect(function () {
    setError(false);
  }, [src]);
  return /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__modal-pic")
  }, /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__modal-box"),
    style: boxStyle
  }, error && /* @__PURE__ */React.createElement(ImageError, {
    errorText: errorText
  }), !error && !!preSrc && /* @__PURE__ */React.createElement("img", {
    className: "".concat(classPrefix, "-image-viewer__modal-image"),
    onMouseDown: function onMouseDown(event) {
      event.stopPropagation();
      _onMouseDown(event);
    },
    src: preSrc,
    style: preImgStyle,
    "data-testid": "img-drag",
    alt: "image",
    draggable: "false"
  }), !error && /* @__PURE__ */React.createElement("img", {
    className: "".concat(classPrefix, "-image-viewer__modal-image"),
    onMouseDown: function onMouseDown(event) {
      event.stopPropagation();
      _onMouseDown(event);
    },
    src: src,
    onLoad: function onLoad() {
      return setLoaded(true);
    },
    onError: function onError() {
      return setError(true);
    },
    style: imgStyle,
    "data-testid": "img-drag",
    alt: "image",
    draggable: "false"
  })));
};
var ROTATE_COUNT = 90;
var ImageModelIcon = function ImageModelIcon(_ref3) {
  var onClick = _ref3.onClick,
    className = _ref3.className,
    disabled = _ref3.disabled,
    isRange = _ref3.isRange,
    name = _ref3.name,
    label = _ref3.label,
    _ref3$size = _ref3.size,
    size = _ref3$size === void 0 ? "16px" : _ref3$size;
  var _useConfig3 = useConfig(),
    classPrefix = _useConfig3.classPrefix;
  var Icons = useIconMap();
  var Icon = Icons[name];
  return /* @__PURE__ */React.createElement("div", {
    className: classNames("".concat(classPrefix, "-image-viewer__modal-icon"), className, _defineProperty({}, "".concat(classPrefix, "-is-disabled"), disabled)),
    onClick: onClick
  }, name && /* @__PURE__ */React.createElement(Icon, {
    size: size,
    className: isRange ? "is-range" : null
  }), label && /* @__PURE__ */React.createElement("span", {
    className: "".concat(classPrefix, "-image-viewer__modal-icon-label")
  }, label));
};
var ImageViewerUtils = function ImageViewerUtils(_ref4) {
  var onZoom = _ref4.onZoom,
    scale = _ref4.scale,
    onZoomOut = _ref4.onZoomOut,
    currentImage = _ref4.currentImage,
    onRotate = _ref4.onRotate,
    onMirror = _ref4.onMirror,
    onReset = _ref4.onReset,
    tipText = _ref4.tipText;
  var _useConfig4 = useConfig(),
    classPrefix = _useConfig4.classPrefix;
  var _useGlobalIcon2 = useGlobalIcon({
      MirrorIcon: MirrorIcon,
      RotationIcon: RotationIcon,
      ImageIcon: ImageIcon
    }),
    MirrorIcon$1 = _useGlobalIcon2.MirrorIcon,
    RotationIcon$1 = _useGlobalIcon2.RotationIcon,
    ImageIcon$1 = _useGlobalIcon2.ImageIcon;
  return /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__utils")
  }, /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__utils-content")
  }, /* @__PURE__ */React.createElement(TooltipLite, {
    className: "".concat(classPrefix, "-image-viewer__utils--tip"),
    content: tipText.mirror,
    showShadow: false
  }, /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__modal-icon")
  }, /* @__PURE__ */React.createElement(MirrorIcon$1, {
    size: "medium",
    onClick: onMirror
  }))), /* @__PURE__ */React.createElement(TooltipLite, {
    className: "".concat(classPrefix, "-image-viewer__utils--tip"),
    content: tipText.rotate,
    showShadow: false
  }, /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__modal-icon")
  }, /* @__PURE__ */React.createElement(RotationIcon$1, {
    size: "medium",
    onClick: function onClick() {
      return onRotate(-ROTATE_COUNT);
    }
  }))), /* @__PURE__ */React.createElement(ImageModelIcon, {
    size: "medium",
    name: "zoom-out",
    onClick: onZoomOut
  }), /* @__PURE__ */React.createElement(ImageModelIcon, {
    className: "".concat(classPrefix, "-image-viewer__utils-scale"),
    size: "medium",
    label: "".concat(scale * 100, "%")
  }), /* @__PURE__ */React.createElement(ImageModelIcon, {
    size: "medium",
    name: "zoom-in",
    onClick: onZoom
  }), /* @__PURE__ */React.createElement(TooltipLite, {
    className: "".concat(classPrefix, "-image-viewer__utils--tip"),
    content: tipText.originsize,
    showShadow: false
  }, /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__modal-icon")
  }, /* @__PURE__ */React.createElement(ImageIcon$1, {
    size: "medium",
    name: "image",
    onClick: function onClick() {
      onReset();
    }
  }))), currentImage.download && /* @__PURE__ */React.createElement(ImageModelIcon, {
    size: "medium",
    name: "download",
    onClick: function onClick() {
      downloadFile(currentImage.mainImage);
    }
  })));
};
var ImageViewerHeader = function ImageViewerHeader(props) {
  var _useConfig5 = useConfig(),
    classPrefix = _useConfig5.classPrefix;
  var images = props.images,
    currentIndex = props.currentIndex,
    onImgClick = props.onImgClick;
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    isExpand = _useState6[0],
    setIsExpand = _useState6[1];
  var transStyle = {
    transform: "translateX(calc(-".concat(currentIndex, " * (40px / 9 * 16 + 4px)))")
  };
  return /* @__PURE__ */React.createElement("div", {
    className: classNames("".concat(classPrefix, "-image-viewer__modal-header"), _defineProperty({}, "".concat(classPrefix, "-is-show"), isExpand))
  }, /* @__PURE__ */React.createElement(ImageModelIcon, {
    name: "chevron-down",
    className: "".concat(classPrefix, "-image-viewer__header-pre-bt"),
    onClick: function onClick() {
      return setIsExpand(!isExpand);
    }
  }), /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__header-prev")
  }, /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__header-trans"),
    style: transStyle
  }, images.map(function (image, index) {
    return /* @__PURE__ */React.createElement("div", {
      key: index,
      className: classNames("".concat(classPrefix, "-image-viewer__header-box"), _defineProperty({}, "".concat(classPrefix, "-is-active"), index === currentIndex)),
      onClick: function onClick() {
        return onImgClick(index);
      }
    }, /* @__PURE__ */React.createElement("img", {
      alt: "",
      src: image.thumbnail || image.mainImage,
      className: "".concat(classPrefix, "-image-viewer__header-img")
    }));
  }))));
};
var ImageModal = function ImageModal(props) {
  var closeOnOverlay = props.closeOnOverlay,
    _props$showOverlay = props.showOverlay,
    showOverlay = _props$showOverlay === void 0 ? true : _props$showOverlay,
    zIndex = props.zIndex,
    images = props.images,
    isMini = props.isMini,
    imageScale = props.imageScale,
    viewerScale = props.viewerScale,
    closeBtn = props.closeBtn,
    draggable = props.draggable,
    onOpen = props.onOpen,
    onClose = props.onClose,
    visible = props.visible,
    title = props.title,
    resProps = _objectWithoutProperties(props, _excluded);
  var _useConfig6 = useConfig(),
    classPrefix = _useConfig6.classPrefix;
  var _useLocaleReceiver = useLocaleReceiver("imageViewer"),
    _useLocaleReceiver2 = _slicedToArray(_useLocaleReceiver, 2),
    locale = _useLocaleReceiver2[0],
    t = _useLocaleReceiver2[1];
  if (resProps.index === void 0) delete resProps.index;
  var _useIndex = useIndex(resProps, images),
    index = _useIndex.index,
    next = _useIndex.next,
    prev = _useIndex.prev,
    setIndex = _useIndex.setIndex;
  var _useRotate = useRotate(),
    rotateZ = _useRotate.rotateZ,
    onResetRotate = _useRotate.onResetRotate,
    onRotate = _useRotate.onRotate;
  var _useScale = useScale(imageScale),
    scale = _useScale.scale,
    onZoom = _useScale.onZoom,
    onZoomOut = _useScale.onZoomOut,
    onResetScale = _useScale.onResetScale;
  var _useMirror = useMirror(),
    mirror = _useMirror.mirror,
    onResetMirror = _useMirror.onResetMirror,
    onMirror = _useMirror.onMirror;
  var onReset = useCallback(function () {
    onResetScale();
    onResetRotate();
    onResetMirror();
  }, [onResetMirror, onResetScale, onResetRotate]);
  var onScroll = useCallback(function (e) {
    var deltaY = e.deltaY;
    deltaY > 0 ? onZoom() : onZoomOut();
  }, [onZoom, onZoomOut]);
  var onKeyDown = useCallback(function (event) {
    switch (event.key) {
      case "ArrowRight":
        return next();
      case "ArrowLeft":
        return prev();
      case "ArrowUp":
        return onZoom();
      case "ArrowDown":
        return onZoomOut();
      case "Escape":
        return onClose === null || onClose === void 0 ? void 0 : onClose({
          trigger: "esc",
          e: event
        });
    }
  }, [next, onClose, prev, onZoom, onZoomOut]);
  useEffect(function () {
    document.addEventListener("keydown", onKeyDown);
    return function () {
      return document.removeEventListener("keydown", onKeyDown);
    };
  }, [onKeyDown]);
  useEffect(function () {
    onReset();
  }, [index, onReset]);
  if (!isArray_1(images) || images.length < 1) return null;
  var currentImage = images[index];
  var tipText = {
    mirror: t(locale.mirrorTipText),
    rotate: t(locale.rotateTipText),
    originsize: t(locale.originalSizeTipText)
  };
  var errorText = t(locale.errorText);
  if (isMini) {
    return /* @__PURE__ */React.createElement(ImageModelMini, {
      visible: visible,
      draggable: draggable,
      index: index,
      images: images,
      imageScale: imageScale,
      viewerScale: viewerScale,
      rotateZ: rotateZ,
      zIndex: zIndex,
      currentImage: currentImage,
      prev: prev,
      next: next,
      mirror: mirror,
      scale: scale,
      title: title,
      onMirror: onMirror,
      onZoom: onZoom,
      onClose: onClose,
      onZoomOut: onZoomOut,
      onScroll: onScroll,
      onReset: onReset,
      onRotate: onRotate,
      errorText: errorText,
      tipText: tipText
    });
  }
  var closeNode = closeBtn;
  if (closeBtn === true) {
    closeNode = /* @__PURE__ */React.createElement(ImageModelIcon, {
      name: "close",
      size: "24px",
      className: "".concat(classPrefix, "-image-viewer__modal-close-bt "),
      onClick: function onClick(e) {
        return onClose && onClose({
          trigger: "close-btn",
          e: e
        });
      }
    });
  } else if (isFunction_1(closeBtn)) closeNode = closeBtn({
    onClose: onClose,
    onOpen: onOpen
  });
  return /* @__PURE__ */React.createElement("div", {
    className: classNames("".concat(classPrefix, "-image-viewer-preview-image"), _defineProperty({}, "".concat(classPrefix, "-is-hide"), !visible)),
    onWheel: onScroll,
    style: {
      zIndex: zIndex
    }
  }, !!showOverlay && /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__modal-mask"),
    onClick: function onClick(e) {
      return closeOnOverlay && (onClose === null || onClose === void 0 ? void 0 : onClose({
        trigger: "overlay",
        e: e
      }));
    }
  }), images.length > 1 && /* @__PURE__ */React.createElement(React.Fragment, null, /* @__PURE__ */React.createElement(ImageViewerHeader, {
    images: images,
    currentIndex: index,
    onImgClick: setIndex
  }), /* @__PURE__ */React.createElement("div", {
    className: "".concat(classPrefix, "-image-viewer__modal-index")
  }, /* @__PURE__ */React.createElement("span", null, title), "".concat(index + 1, "/").concat(images.length)), /* @__PURE__ */React.createElement(ImageModelIcon, {
    size: "24px",
    name: "chevron-left",
    className: "".concat(classPrefix, "-image-viewer__modal-prev-bt"),
    onClick: prev,
    disabled: index <= 0
  }), /* @__PURE__ */React.createElement(ImageModelIcon, {
    size: "24px",
    name: "chevron-right",
    className: "".concat(classPrefix, "-image-viewer__modal-next-bt"),
    onClick: next,
    disabled: index >= images.length - 1
  })), /* @__PURE__ */React.createElement(ImageViewerUtils, {
    onZoom: onZoom,
    onZoomOut: onZoomOut,
    scale: scale,
    currentImage: currentImage,
    onRotate: onRotate,
    onMirror: onMirror,
    onReset: onReset,
    tipText: tipText
  }), closeNode, /* @__PURE__ */React.createElement(ImageModelItem, {
    scale: scale,
    rotateZ: rotateZ,
    mirror: mirror,
    preSrc: currentImage.thumbnail,
    src: currentImage.mainImage,
    errorText: errorText
  }));
};

export { ImageModal as I, ImageModelMiniContent as a, ImageModelMini as b, ImageModelItem as c, ImageViewerUtils as d };
//# sourceMappingURL=dep-10388ab5.js.map
